import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {RouteComponentProps} from 'react-router'
import {Redirect, Route, Switch} from 'react-router-dom'

import {LocationDescriptorObject} from 'history'
import {useProductLine} from 'hooks/useProductLine'
import {TFunction} from 'i18next'

import {Navigator} from '@progyny/components/Navigator'
import PageLoader from '@progyny/components/PageLoader'
import useOnboardingMutations from '@progyny/hooks/useOnboardingMutations'
import {useProgressColor} from '@progyny/hooks/useProgressColor'
import BirthDate from '@progyny/pages/CreateAccount/BirthDate'
import City from '@progyny/pages/CreateAccount/City'
import CommonContainer from '@progyny/pages/CreateAccount/CommonContainer'
import Complete from '@progyny/pages/CreateAccount/Complete'
import Consent from '@progyny/pages/CreateAccount/Consent'
import Preferences from '@progyny/pages/CreateAccount/Preferences'
import PrivacyAlertBox from '@progyny/pages/CreateAccount/PrivacyAlertBox'
import {
  CreateAccountRoutes,
  CreateAccountSubRoute,
} from '@progyny/pages/CreateAccount/types'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'
import {getResumeRoute} from '@progyny/utils/getOnboardingResumePath'

import {isNotLang} from '../../utils'

const generateRoutes = ({t}: {t: TFunction}): CreateAccountSubRoute[] => {
  return [
    {
      path: '/terms',
      component: Consent,
      headerTitle: t('create_account_transparency_is_important_to_us'),
      showProgressBar: true,
    },
    {
      path: '/birth-date',
      component: BirthDate,
      headerTitle: t('create_account_when_were_you_born'),
      description: t('create_account_we_need_your_date_of'),
      showProgressBar: true,
    },
    {
      path: '/city',
      component: City,
      headerTitle: t('create_account_in_which_city_do_you'),
      description: t('create_account_we_use_this_information_to'),
      showProgressBar: true,
    },
    {
      path: '/preferences',
      component: Preferences,
      headerTitle: t('create_account_your_preferences'),
      description: t('create_account_we_use_this_information_to_2'),
      showProgressBar: true,
    },
    {
      path: '/complete',
      component: Complete,
      showProgressBar: false,
    },
  ]
}

const CreateAccount: React.FC<RouteComponentProps> = (props) => {
  // Provide default value for create account procedure
  const loading = useMeStore((s) => s.loading)
  const isAuthorized = useMeStore((s) => s.isAuthorized)
  const isLoggedIn = useMeStore((s) => s.isLoggedIn)
  const isAccountCreated = useMeStore((s) => s.isAccountCreated)
  const isEmailConfirmed = useMeStore((s) => s.isEmailConfirmed)
  const onboardingData = useMeStore((s) => s.onboardingData)
  const user = useMeStore((s) => s.user)
  const isInConservativeCountry = useMeStore((s) => s.isInConservativeCountry)
  const {history, match, location} = props
  const url = match.url
  const {t} = useTranslation()
  const {getPath} = useRoutes()
  const {productLine} = useProductLine()
  const progressColor = useProgressColor()

  const {updateLoading, persistCreateAccountData} = useOnboardingMutations()

  const hasAcceptedTermsAndConditions =
    onboardingData.acceptPrivacyStatement &&
    onboardingData.acceptTermsConditions
  const hasCity = Boolean(onboardingData.city)
  const hasBirthdate = Boolean(onboardingData.birthDate)

  const subRoutes = useMemo(() => {
    return generateRoutes({t})
  }, [t])
  let subRouteIndex: CreateAccountRoutes

  if (!hasAcceptedTermsAndConditions) {
    subRouteIndex = CreateAccountRoutes.CONSENT
  } else if (!hasBirthdate) {
    subRouteIndex = CreateAccountRoutes.BIRTH_DATE
  } else if (!hasCity) {
    subRouteIndex = CreateAccountRoutes.CITY
  } else {
    subRouteIndex = CreateAccountRoutes.COMPLETE
  }

  function getRedirectPath() {
    const {resumePath} = getResumeRoute(user?.member, productLine, {
      isConservativeCountry: isInConservativeCountry(),
    })

    let pathOrLocation: string | LocationDescriptorObject
    if (isAuthorized) {
      pathOrLocation = getPath(RouteEnum.HOME)
    } else if (isAccountCreated) {
      pathOrLocation =
        resumePath ?? `${url}${subRoutes[CreateAccountRoutes.PREFERENCES].path}`
    } else if (isEmailConfirmed) {
      pathOrLocation = resumePath ?? `${url}${subRoutes[subRouteIndex].path}`
    } else if (isLoggedIn) {
      pathOrLocation = {
        pathname: getPath(RouteEnum.CONFIRM_EMAIL),
        state: {email: user?.email},
      }
    } else {
      pathOrLocation = `${getPath(RouteEnum.LOGIN)}?next=${encodeURI(url)}`
    }

    return pathOrLocation
  }

  if (loading) {
    return <PageLoader position={'top'} />
  }

  const pathnameNormalized = location.pathname.replace(/\/$/, '') || '/'
  const termsPath = `${url.replace(/\/$/, '')}/terms`
  const isOnTermsPath = pathnameNormalized === termsPath

  if (isOnTermsPath && (!isLoggedIn || !isEmailConfirmed)) {
    if (!isLoggedIn) {
      return (
        <Redirect
          to={`${getPath(RouteEnum.LOGIN)}?next=${encodeURI(
            location.pathname + location.search
          )}`}
        />
      )
    }
    return (
      <Redirect
        to={{
          pathname: getPath(RouteEnum.CONFIRM_EMAIL),
          state: {email: user?.email},
        }}
      />
    )
  }

  if (!hasAcceptedTermsAndConditions && pathnameNormalized !== termsPath) {
    return <Redirect to={termsPath} />
  }

  return (
    <Switch>
      {subRoutes.map((route, idx) => {
        const Comp = route.component

        return (
          <Route
            key={idx}
            path={`${url}${route.path}`}
            render={() => (
              <CommonContainer
                progressColor={progressColor}
                progressBar={route.showProgressBar}
                progressBarTitle={route.progressBarTitle}
                headerTitle={route?.headerTitle}
                description={route?.description}
                progressValue={((idx + 1) / subRoutes.length) * 100}
                alertBox={
                  route.path === '/terms' && isNotLang('en') ? (
                    <PrivacyAlertBox />
                  ) : undefined
                }
                align={route.path === '/terms' ? 'top' : undefined}
                maxWidth={route.path === '/terms' ? '900px' : '600px'}
              >
                <Comp
                  navigator={Navigator}
                  initialValue={onboardingData}
                  callback={() => {
                    if (idx + 1 > subRoutes.length - 1)
                      history.push(getPath(RouteEnum.SETUP_PROFILE))
                    else history.push(`${url}${subRoutes[idx + 1].path}`)
                  }}
                  updateLoading={updateLoading}
                  persistMemberData={persistCreateAccountData}
                  goBack={() => {
                    history.push(`${url}${subRoutes[idx - 1].path}`)
                  }}
                />
              </CommonContainer>
            )}
          />
        )
      })}
      <Route exact path={match.url}>
        <Redirect to={getRedirectPath()} />
      </Route>
    </Switch>
  )
}
export default CreateAccount
